import {
  Participant,
  ParticipantCreatePayload,
  ParticipantResponse,
  ParticipantSaveResponse,
  SearchParticipantsResponse,
  ParticipantUpdatePayload,
  ProgramOrgUnits,
  SearchParticipantsPayload,
} from "@/domain/participant.interface";
import { axios, transformAxiosError } from "@/utils/axios.create";
import { parseEnrollmentStatus } from "./utils/enrollmentStatus.utils";

const findById = async (id: string): Promise<ParticipantResponse> => {
  return axios
    .get(`/participants/${id}`)
    .then((res) => {
      return (res.data.body as ParticipantResponse) || [];
    })
    .catch((err) => {
      return Promise.reject(transformAxiosError(err));
    });
};

const search = async (
  params: SearchParticipantsPayload,
): Promise<SearchParticipantsResponse> => {
  // Build query parameters
  const queryParams: Record<string, string> = {
    pageNumber: String((params.pageNumber || 0) + 1), // Backend uses 1-based pagination
    pageSize: String(params.pageSize || 10),
    sortField: params.sortField || "firstName",
    sortOrder: params.sortOrder || "asc",
  };

  if (params.search) {
    queryParams.search = params.search;
  }

  if (params.notEnrolled) {
    queryParams.notEnrolled = `${params.notEnrolled}`;
  } else {
    if (params.programIds && params.programIds.length > 0) {
      queryParams.programIds = params.programIds.join(",");
    }

    if (params.orgUnitId) {
      queryParams.orgUnitIds = params.orgUnitId;
    }

    if (params.groupLabelId) {
      queryParams.groupLabelIds = params.groupLabelId;
    }

    if (params.status && params.status.length > 0) {
      // Map frontend status format to backend format (Active -> ACTIVE)
      const backendStatuses = params.status.map(parseEnrollmentStatus);
      queryParams.status = backendStatuses.join(",");
    }
  }

  return axios
    .get("/participants/search", { params: queryParams })
    .then((res) => {
      return (res.data.body as SearchParticipantsResponse) || [];
    })
    .catch((err) => {
      return Promise.reject(transformAxiosError(err));
    });
};

const create = async (
  payload: ParticipantCreatePayload,
): Promise<ParticipantSaveResponse> => {
  return axios
    .post("/participants", payload)
    .then((res) => {
      return mergeGeocodeSignals(res.data);
    })
    .catch((err) => {
      return Promise.reject(transformAxiosError(err));
    });
};

const update = async ({
  id,
  payload,
}: {
  id: string;
  payload: ParticipantUpdatePayload;
}): Promise<ParticipantSaveResponse> => {
  return axios
    .put(`/participants/${id}`, payload)
    .then((res) => {
      return mergeGeocodeSignals(res.data);
    })
    .catch((err) => {
      return Promise.reject(transformAxiosError(err));
    });
};

const mergeGeocodeSignals = (data: {
  body?: Participant;
  geocodedPrimary?: boolean;
  geocodedSecondary?: boolean;
  geocodePrimaryError?: ParticipantSaveResponse["geocodePrimaryError"];
  geocodeSecondaryError?: ParticipantSaveResponse["geocodeSecondaryError"];
}): ParticipantSaveResponse => {
  const participant = (data?.body as Participant) || ({} as Participant);
  return {
    ...participant,
    geocodedPrimary: data?.geocodedPrimary,
    geocodedSecondary: data?.geocodedSecondary,
    geocodePrimaryError: data?.geocodePrimaryError,
    geocodeSecondaryError: data?.geocodeSecondaryError,
  };
};

const getProgramOrgUnits = async (
  participantId: string,
  userId: string,
  editing?: boolean,
): Promise<ProgramOrgUnits[]> => {
  return axios
    .get(
      `/participants/${participantId}/programs?userId=${userId}&editing=${editing}`,
    )
    .then((res) => res.data.body)
    .catch((err) => {
      throw new Error(
        err.response?.data?.message || "Failed to fetch enrollments",
      );
    });
};

const softDelete = async (id: string): Promise<Participant> => {
  return axios
    .delete(`/participants/${id}`)
    .then((res) => res.data.body)
    .catch((err) => {
      throw new Error(
        err.response?.data?.message || "Failed to soft delete participant",
      );
    });
};

const globalSearch = async ({
  search,
  userId,
}: {
  search: string;
  userId: string;
}): Promise<Participant[]> => {
  const queryParams = new URLSearchParams({
    userId,
    search,
  });

  return axios
    .get(`/participants/global-search?${queryParams.toString()}`)
    .then((res) => {
      return (res.data.body as Participant[]) || [];
    })
    .catch((err) => {
      return Promise.reject(transformAxiosError(err));
    });
};

const getRedeemedItems = async (participantId: string): Promise<any[]> => {
  return axios
    .get(`/participants/${participantId}/redeemed-items`)
    .then((res) => res.data.body || [])
    .catch((err) => {
      console.error("Failed to fetch redeemed items:", err);
      return []; // Return empty array on error
    });
};

export const ParticipantsService = {
  findById,
  search,
  create,
  update,
  getProgramOrgUnits,
  softDelete,
  globalSearch,
  getRedeemedItems,
};
